import React from "react";
import { motion } from "framer-motion";
import {
  Store,
  ShoppingCart,
  Hotel,
  Cake,
  Truck,
  Factory,
  Scale,
} from "lucide-react";

const CHEF_IMG =
  "https://images.pexels.com/photos/36981211/pexels-photo-36981211.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const INDUSTRIES = [
  { icon: Store, name: "Retail Stores", desc: "Premium-grade pack sizes for storefront display." },
  { icon: ShoppingCart, name: "Supermarkets", desc: "Private-label & bulk SKU programmes." },
  { icon: Hotel, name: "Hotels", desc: "Five-star kitchens & banqueting consistency." },
  { icon: Cake, name: "Sweet Shops", desc: "Aroma-rich pods for halwas, kheers & mithai." },
  { icon: Truck, name: "Distributors", desc: "Container-load contracts pan-Karnataka." },
  { icon: Factory, name: "Food Manufacturers", desc: "Spec-locked supply for industrial blends." },
  { icon: Scale, name: "Spice Traders", desc: "Wholesale lots, competitive market pricing." },
];

export const IndustriesServed = () => {
  return (
    <section
      id="industries"
      data-testid="industries-section"
      className="section-pad bg-[#FAF8F2]"
    >
      <div className="container-luxury">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
          <div className="md:col-span-7">
            <div className="heading-eyebrow">
              <span className="line" />
              <span className="text">Who We Supply</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl text-[#0E4D3D] leading-tight">
              Industries we serve
              <br />
              <span className="italic font-light text-[#C9A227]">
                across Bangalore.
              </span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-[#4A4A4A] text-base md:text-lg font-light leading-relaxed">
              From boutique sweet shops in Malleshwaram to five-star kitchens
              on MG Road and food-processing units in Bommasandra — every
              segment is served with grade-matched precision.
            </p>
          </div>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 grid-rows-[auto_auto_auto] gap-4">
          {/* Hero image cell */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="col-span-2 row-span-2 relative overflow-hidden group min-h-[320px] md:min-h-[420px]"
          >
            <img
              src={CHEF_IMG}
              alt="Premium kitchen using cardamom"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1500ms]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#07271E] via-[#07271E]/40 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-7 md:p-10">
              <div className="chip">Featured Segment</div>
              <h3 className="font-serif text-3xl md:text-4xl text-white mt-4 leading-tight">
                Hospitality &<br />Fine-Dining
              </h3>
              <p className="text-white/75 text-sm md:text-base mt-3 max-w-md font-light">
                The aroma your chefs trust for biryani, payasam, masala chai
                and signature desserts.
              </p>
            </div>
          </motion.div>

          {INDUSTRIES.map((ind, i) => {
            const Icon = ind.icon;
            return (
              <motion.div
                key={ind.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.06, duration: 0.5 }}
                className="bg-white p-6 md:p-7 border border-[#0E4D3D]/10 hover:border-[#C9A227] hover:bg-[#0E4D3D] hover:text-white transition-all duration-500 group lift"
                data-testid={`industry-${i}`}
              >
                <Icon
                  className="w-7 h-7 text-[#0E4D3D] group-hover:text-[#C9A227] transition-colors"
                  strokeWidth={1.3}
                />
                <h3 className="mt-5 font-serif text-lg md:text-xl text-[#0E4D3D] group-hover:text-white transition-colors">
                  {ind.name}
                </h3>
                <p className="mt-2 text-[#4A4A4A] text-xs md:text-sm font-light leading-relaxed group-hover:text-white/75 transition-colors">
                  {ind.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
